"use client";

import { AppEnv, ProcessorType } from "@autumn/shared";
import { ClockIcon } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { useState } from "react";
import { Link } from "react-router";
import { RevenueCatIcon } from "@/components/v2/icons/AutumnIcons";
import { SheetBackdrop } from "@/components/v2/sheets/SheetBackdrop";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/v2/tooltips/Tooltip";
import { useCusRewardsQuery } from "@/hooks/queries/useCusRewardsQuery";
import { useSheetStore } from "@/hooks/stores/useSheetStore";
import { useEntity } from "@/hooks/stores/useSubscriptionStore";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { useEnv } from "@/utils/envUtils";
import { pushPage } from "@/utils/genUtils";
import ErrorScreen from "@/views/general/ErrorScreen";
import LoadingScreen from "@/views/general/LoadingScreen";
import { useOnboardingVisibility } from "@/views/onboarding4/hooks/useOnboardingProgress";
import { OnboardingGuide } from "@/views/onboarding4/OnboardingGuide";
import { useCusQuery } from "../../customers/customer/hooks/useCusQuery";
import { useCusReferralQuery } from "../../customers/customer/hooks/useCusReferralQuery";
import { CustomerBillingControlsSection } from "../components/CustomerBillingControlsSection";
import { CustomerPlansSection } from "../components/CustomerPlansSection";
import { CustomerFeatureUsageTable } from "../components/table/customer-feature-usage/CustomerFeatureUsageTable";
import { CustomerInvoicesTable } from "../components/table/customer-invoices/CustomerInvoicesTable";
import { CustomerUsageAnalyticsTable } from "../components/table/customer-usage-analytics/CustomerUsageAnalyticsTable";
import { CustomerBreadcrumbs } from "./CustomerBreadcrumbs2";
import { CustomerContext } from "./CustomerContext";
import { CustomerPageDetails } from "./CustomerPageDetails";
import { CustomerSheets } from "./CustomerSheets";
import { CustomerHeaderActions } from "./components/CustomerHeaderActions";
import { SelectedEntityDetails } from "./components/SelectedEntityDetails";
import { SHEET_ANIMATION } from "./customerAnimations";
import { Workbench } from "./workbench/Workbench";

export default function CustomerView2() {
	const {
		customer,
		schedule,
		testClockFrozenTimeMs,
		isLoading: cusLoading,
	} = useCusQuery();

	useCusReferralQuery();
	useCusRewardsQuery();
	const { entityId, setEntityId } = useEntity();

	const sheetType = useSheetStore((s) => s.type);
	const closeProductSheet = useSheetStore((s) => s.closeSheet);
	const isMobile = useIsMobile();
	const [isInlineEditorOpen, setIsInlineEditorOpen] = useState(false);

	const env = useEnv();
	const { isDismissed } = useOnboardingVisibility();
	const showOnboarding = env === AppEnv.Sandbox && !isDismissed;

	if (cusLoading && !customer) return <LoadingScreen />;

	if (!customer) {
		return (
			<ErrorScreen>
				<div className="text-muted-foreground text-sm">Customer not found</div>
				<Link
					className="text-tertiary-foreground text-xs hover:underline"
					to={pushPage({ path: "/customers" })}
				>
					Return
				</Link>
			</ErrorScreen>
		);
	}

	const isRevenueCatCustomer = customer.customer_products.some(
		(cp) => cp.processor?.type === ProcessorType.RevenueCat,
	);

	return (
		<CustomerContext.Provider
			value={{
				customer,
				entityId: entityId,
				setEntityId,
				isInlineEditorOpen,
				setIsInlineEditorOpen,
			}}
		>
			<div className="flex w-full h-full overflow-hidden relative">
				<motion.div
					className="h-full overflow-hidden absolute inset-0 z-0"
					animate={{
						width: sheetType && !isMobile ? "calc(100% - 28rem)" : "100%",
					}}
					transition={SHEET_ANIMATION}
				>
					<div className="flex flex-col overflow-x-hidden overflow-y-auto absolute inset-0 pb-8">
						{showOnboarding && (
							<div className="w-full max-w-5xl mx-auto pt-4 sm:pt-8 px-4 sm:px-10">
								<OnboardingGuide />
							</div>
						)}
						<div
							className={cn(
								"flex flex-col gap-4 w-full max-w-5xl mx-auto px-4 sm:px-10",
								showOnboarding ? "pt-4" : "pt-4 sm:pt-8",
							)}
						>
							<div className="flex flex-col gap-2 w-full">
								<div className="flex flex-col w-full">
									<div className="flex items-center justify-between w-full gap-4">
										<CustomerBreadcrumbs />
										<CustomerHeaderActions />
									</div>
									<div className="flex items-center justify-between w-full pt-2 gap-2">
										<div className="flex items-center gap-2 min-w-0">
											<h3
												className={`text-md font-semibold truncate min-w-0 ${
													customer.name
														? "text-foreground"
														: customer.email
															? "text-tertiary-foreground"
															: "text-subtle font-mono font-medium!"
												}`}
											>
												{customer.name || customer.email || customer.id}
											</h3>
											{Boolean(customer.processors?.vercel) && (
												<TooltipProvider>
													<Tooltip delayDuration={0}>
														<TooltipTrigger>
															<svg
																fill="currentColor"
																xmlns="http://www.w3.org/2000/svg"
																viewBox="0 0 1155 1000"
																className="w-3 h-3 text-black dark:text-white"
															>
																<title>Vercel Marketplace Customer</title>
																<path d="m577.3 0 577.4 1000H0z" />
															</svg>
														</TooltipTrigger>
														<TooltipContent>
															<span>Vercel Marketplace Customer</span>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											)}
											{isRevenueCatCustomer && (
												<TooltipProvider>
													<Tooltip delayDuration={0}>
														<TooltipTrigger>
															<span className="text-[#ff5f45] dark:text-[#ff8b78]">
																<RevenueCatIcon size={12} />
															</span>
														</TooltipTrigger>
														<TooltipContent>
															<span>RevenueCat Customer</span>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											)}
											{testClockFrozenTimeMs != null && (
												<TooltipProvider>
													<Tooltip delayDuration={0}>
														<TooltipTrigger asChild>
															<span className="flex shrink-0 items-center justify-center size-5 rounded-md bg-orange-500/15">
																<ClockIcon
																	size={12}
																	weight="bold"
																	className="text-orange-500"
																/>
															</span>
														</TooltipTrigger>
														<TooltipContent>
															Test clock:{" "}
															{new Date(testClockFrozenTimeMs).toLocaleString(
																undefined,
																{
																	month: "short",
																	day: "numeric",
																	year: "numeric",
																	hour: "numeric",
																	minute: "2-digit",
																},
															)}
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											)}
										</div>

										<CustomerPageDetails />
									</div>
								</div>
								<SelectedEntityDetails />
							</div>
							<div className="flex flex-col gap-16 w-full">
								<CustomerPlansSection />
								<CustomerFeatureUsageTable />
								{!entityId && <CustomerUsageAnalyticsTable />}
								<CustomerBillingControlsSection />
								{!entityId && <CustomerInvoicesTable />}
							</div>
						</div>
					</div>
					{!isMobile && (
						<SheetBackdrop
							isOpen={!!sheetType && !isInlineEditorOpen}
							onClose={closeProductSheet}
						/>
					)}
				</motion.div>

				<CustomerSheets />
				<Workbench />
			</div>
		</CustomerContext.Provider>
	);
}
